import Commons from './Commons';
import { DRAWING_MODE, DRAWING_MODE_MIXPANEL, LAYER, LAYER_NZ, METERS_PER_PIXEL_AT_ZOOM_LEVEL_0 } from '../constants/map';
import { COUNTRY, isNZ } from '../constants/crux';
import polygonClipping from 'polygon-clipping';

const getCatchmentPath = (shapeStr) => {
    const polygons = [];
    const parser = new DOMParser();
    const parsedPolygon = parser.parseFromString(shapeStr, 'text/xml');
    // Format of received coordinates is as follows:
    // <coordinates>153.050617,-27.443688999999367 153.05066,......,-27.443688999999367
    // First and last elements are not pairs, rather longitude and latitude respectively
    const coordinates = parsedPolygon.getElementsByTagName('coordinates');
    if (coordinates && coordinates.length > 0) {
        for (const coordinate of coordinates) {
            if (coordinate.childNodes[0] && coordinate.childNodes[0].nodeValue) {
                const path = [];
                const latlng = coordinate.childNodes[0].nodeValue.trim().split(/\s+/);
                latlng.forEach((element) => {
                    const point = element.trim().split(',');
                    const node = { lng: Number(point[0].trim()) };
                    if (point[1]) {
                        node.lat = Number(point[1].trim());
                    }
                    path.push(node);
                });

                polygons.push(path);
            }
        }
    }

    return polygons;
};
const getCatchmentPaths = (catchmentSchools, activeListId) => {
    const paths = [];
    if (catchmentSchools && catchmentSchools.length > 0) {
        catchmentSchools.forEach((catchmentSchool) => {
            const { schoolDetails } = catchmentSchool;
            if (schoolDetails) {
                let type = 'Primary';
                const { placeMetaData, placeId, shape } = schoolDetails;
                const { school } = placeMetaData;
                const displaySelectedSchool = !activeListId || placeId === activeListId;

                if (!displaySelectedSchool) return;
                if (placeMetaData && school && school.length > 0) {
                    type = school[0].schoolType ? school[0].schoolType : type;
                }
                paths.push({
                    id: placeId,
                    path: getCatchmentPath(shape),
                    type,
                });
            }
        });
    }
    return paths;
};

const getPropertyBubbleHeight = (length = 1) => {
    // if bubble is no longer centered
    // double check height values
    const propertyContentHeight = 101;
    const iconHeight = 35;
    const bubbleMarginBottom = 10;
    const groupedPropertySummaryHeight = 38;
    let total = ((propertyContentHeight * length) + bubbleMarginBottom + iconHeight);
    if (length > 1) {
        total += groupedPropertySummaryHeight;
    }

    return total;
};

const getSchoolBubbleHeight = (isTargetProperty = false) => {
    const schoolBubbleHeight = 62;
    const schoolBubbleIconHeight = 45;
    const targetPropertyBubbleHeight = 44;
    const targetPropertyIconHeight = 35;
    const bubbleMarginBottom = 10;
    let total = bubbleMarginBottom + schoolBubbleHeight + schoolBubbleIconHeight;
    if (isTargetProperty) {
        total = bubbleMarginBottom + targetPropertyBubbleHeight + targetPropertyIconHeight;
    }

    return total;
};

const centerBubbleInMap = (mapInstance, coordinates, bubbleHeight) => {
    mapInstance.panTo(coordinates);
    mapInstance.panBy(0, (bubbleHeight / 2) * -1);
};

const addCoordinateToBounds = (marker, bounds, key = '') => {
    if (marker instanceof Array) {
        let _markers = [];
        _markers = _markers.concat(marker);
        _markers.forEach((_marker) => {
            const coordinate = key ? Commons.get(_marker, key) : _marker;
            bounds.extend({
                lat: coordinate.latitude || coordinate.lat,
                lng: coordinate.longitude || coordinate.lng,
            });
        });
    } else {
        bounds.extend({
            lat: marker.latitude || marker.lat,
            lng: marker.longitude || marker.lng,
        });
    }
};

const addPolygonToBounds = (polygon, bounds) => {
    if (polygon instanceof Array) {
        polygon.forEach((schoolCatchment) => {
            if (schoolCatchment.path && schoolCatchment.path.length > 0) {
                schoolCatchment.path.forEach((coordinate) => {
                    if (coordinate && coordinate.length > 0) {
                        addCoordinateToBounds(coordinate, bounds);
                    }
                });
            }
        });
    } else {
        polygon.path.forEach((coordinate) => {
            if (coordinate && coordinate.length > 0) {
                addCoordinateToBounds(coordinate, bounds);
            }
        });
    }
};

const convertMetersToKm = (num) => {
    if (!num || num === 0) return num;

    if (num > 999) {
        return `${(num / 1000).toFixed(3)}km`;
    }
    return `${num.toFixed(2)}m`;
};

const getLayerNames = (layers = []) => {
    const layerNames = [];
    let mapLayer;
    try {
        const countryLayer = isNZ ? LAYER_NZ : LAYER;
        layers.forEach((layer) => {
            Object.keys(countryLayer).forEach((category) => {
                mapLayer = countryLayer[category].find(_layer => _layer.id === layer.id);
                if (mapLayer) layerNames.push(mapLayer.label);
            });
        });
    } catch (err) {
        // err
    }

    return layerNames;
};

const getCoordinateFromGeocoder = (address, callback) => {
    try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({
            address, componentRestrictions: { country: COUNTRY },
        }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
                const latLng = results[0];
                const { lat, lng } = latLng.geometry.location.toJSON();
                callback({ lat, lng });
            } else {
                callback(null);
            }
        });
    } catch (err) {
        callback(null);
    }
};

const hasGoogleMap = () => {
    try {
        if (typeof window.google === 'object' &&
            typeof window.google.maps === 'object') {
            return true;
        }
    } catch (err) {
        // let it proceed and return false;
    }

    return false;
};

const getCoordinateFromPropertyCommons = (propertyCommons) => {
    const location = Commons.get(propertyCommons, 'location');

    if (location) {
        const { latitude, longitude } = location;

        if (latitude && longitude) {
            return {
                lat: parseFloat(latitude),
                lng: parseFloat(longitude),
            };
        }
    }

    return {};
};

const getDistanceFromTarget = (targetProperty, property) => {
    const { lat, lon } = property || {};
    const {
        lat: targetPropertyLat,
        lon: targetPropertyLon,
    } = targetProperty || {}
    const point1 = new window.google.maps.LatLng(lat, lon);
    const point2 = new window
        .google
        .maps
        .LatLng(targetPropertyLat, targetPropertyLon);

    return Math.round((window.google.maps.geometry.spherical.computeDistanceBetween(
        point1,
        point2
    ) / 1000) * 100)  / 100;
};

const convertDegreesToRadians = (degrees) => {
    return degrees * (Math.PI / 180);
};
const getMetersPerPixelByZoomLevel = (radians, zoomLevel) => {
    const metersPerPixel = METERS_PER_PIXEL_AT_ZOOM_LEVEL_0 * Math.cos(radians);
    const googleMapResolutionScale = Math.pow(2, zoomLevel);

    return metersPerPixel / googleMapResolutionScale;
};
const convertMetersToPixels = (meters, zoomLevel, latitude) => {
    const radians = convertDegreesToRadians(latitude);
    const metersInAPixel = getMetersPerPixelByZoomLevel(radians, zoomLevel);

    return meters / metersInAPixel;
}

const isPolygonClockwise = (polygon) => {
    let sum = 0;
    for (let i = 0; i < polygon.length - 1; i++) {
        let p1 = polygon[i];
        let p2 = polygon[i + 1];
        sum += (p2.lng - p1.lng) * (p2.lat + p1.lat);
    }
    return sum > 0;
}

const closePolygonPath = (polygon) => {
    if (polygon[0].lat !== polygon[polygon.length - 1].lat || polygon[0].lng !== polygon[polygon.length - 1].lng) {
        polygon.push(polygon[0]);
    }
    return polygon;
}

const convertPolygonToCounterClockwise = (polygon) => {
    return isPolygonClockwise(polygon) ? polygon.reverse() : polygon;
}

// if there are intersections in the polygon, split it into individual polygons for googleMap to render
const splitSelfIntersectingPolygon = (polygon) => {
    const inputPolygon = [polygon.map(coord => [coord.lng, coord.lat])];
    const fixedPolygons = polygonClipping.union(inputPolygon);

    return fixedPolygons.map(ring =>
        ring[0].map(coord => ({ lat: coord[1], lng: coord[0] }))
    );
};

const validateGooglePolygon = (polygon) => {
    if (polygon && polygon.length) {

        const closedPolygon = closePolygonPath(polygon);
        const splitPolygon = splitSelfIntersectingPolygon(closedPolygon);

        return splitPolygon.map(convertPolygonToCounterClockwise);
    } else {
        return polygon;
    }
}

const convertCircleToPolygon = (center, radius, numberOfPoints = 720) => {
    const points = [];
    const angleStep = (2 * Math.PI) / numberOfPoints;
    const earthRadius = 6378137; // in meters

    const centerLat = center.lat();
    const centerLng = center.lng();

    for (let i = 0; i < numberOfPoints; i++) {
        const angle = i * angleStep;
        const dx = radius * Math.cos(angle);
        const dy = radius * Math.sin(angle);

        const dLat = dy / earthRadius;
        const dLng = dx / (earthRadius * Math.cos(centerLat * Math.PI / 180));

        points.push({
            lat: centerLat + dLat * (180 / Math.PI),
            lng: centerLng + dLng * (180 / Math.PI),
        });
    }

    return points;
}

// Converts bbox to an array of coordinates
export const Bbox = {
    toArray: (bounds) => {
        if (!bounds || !bounds.getNorthEast || !bounds.getSouthWest) {
            throw new Error('Invalid bounds object');
        }

        const NE = {
            lat: bounds.getNorthEast().lat(),
            lon: bounds.getNorthEast().lng(),
        };
        const SW = {
            lat: bounds.getSouthWest().lat(),
            lon: bounds.getSouthWest().lng(),
        };
        const NW = {
            lat: NE.lat,
            lon: SW.lon,
        };
        const SE = {
            lat: SW.lat,
            lon: NE.lon,
        };

        return [NW, NE, SE, SW];
    },
}

export const hasShapesAdded = (searchFilters) => {
    const { mapSearch } = searchFilters || {};

    return !!(mapSearch?.polygon?.length ||
        mapSearch?.circle?.length ||
        (mapSearch?.propertyIds && (
            Array.isArray(mapSearch.propertyIds)
                ? mapSearch.propertyIds.length
                : Object.keys(mapSearch.propertyIds).length
        )));
};

const getShapesCount = (searchFilters) => {
    const { mapSearch } = searchFilters || {};
    let count = 0;

    if (mapSearch?.polygon?.length) count += mapSearch.polygon.length;
    if (mapSearch?.circle?.length) count += mapSearch.circle.length;
    if (mapSearch?.propertyIds) {
        count += Array.isArray(mapSearch.propertyIds)
            ? mapSearch.propertyIds.length
            : Object.keys(mapSearch.propertyIds).length;
    }

    return count;
};

export const getSearchStringFromShapes = ({ rectangle, circle, polygon, polyline }) => {
    const shapes = { rectangle, circle, polygon, polyline };
    const modes = [DRAWING_MODE.CIRCLE, DRAWING_MODE.RECTANGLE, DRAWING_MODE.POLYLINE, DRAWING_MODE.POLYGON];

    return modes
        .filter(mode => shapes[mode]?.length)
        .map(mode => DRAWING_MODE_MIXPANEL[mode]);
};

export default {
    getCatchmentPaths,
    getPropertyBubbleHeight,
    centerBubbleInMap,
    addCoordinateToBounds,
    addPolygonToBounds,
    getSchoolBubbleHeight,
    convertMetersToKm,
    getLayerNames,
    getCoordinateFromGeocoder,
    hasGoogleMap,
    getCoordinateFromPropertyCommons,
    getDistanceFromTarget,
    closePolygonPath,
    convertPolygonToCounterClockwise,
    splitSelfIntersectingPolygon,
    validateGooglePolygon,
    convertMetersToPixels,
    convertCircleToPolygon,
    hasShapesAdded,
    getShapesCount,
    getSearchStringFromShapes,
    Bbox,
};
